import type { InspectionType, TypeEligibility } from './api/inspectionTypes.gen';

// Shared reading of an inspection type's SERVER-RESOLVED eligibility (P03 §7.2,
// story 3-3b).
//
// This file exists so the Settings surface (src/modules/inspectionTypes) and the
// TypePicker (src/components/inspections) say the SAME thing about the same
// reason code without importing each other — a module is reached through the
// host, never by path (P18 §1), so anything both need lives out here.
//
// NOTHING HERE DECIDES ANYTHING. `eligibility.reasons` is computed by
// `backend/src/services/inspectionType.service.js → eligibilityResolver`, from
// the very inputs `assertMayPerform` guards with, and is served per caller on
// every row. The client only TRANSLATES those codes into sentences. P03 §7.2:
// "TypePicker is discovery, never authorization" — a client that re-derived the
// intersection would be a second copy of an authorization rule, i.e. a
// divergence waiting to happen, and it would still be refused by the server.

export type EligibilityReason = TypeEligibility['reasons'][number];

// One sentence per reason code, in the order of "what would I do about it".
// Keyed by the generated union, so a new reason code on the contract is a
// compile error here rather than a silently missing explanation.
export const ELIGIBILITY_REASON_TEXT: Record<EligibilityReason, string> = {
  permission_missing: "You don't have permission to create inspections.",
  role_not_permitted: 'Your role may not perform this type of inspection.',
  type_inactive: 'This inspection type is deactivated.',
  no_selectable_template: 'This inspection type has no selectable template.',
};

/**
 * The visible reason a card is disabled. Every reason is shown, not just the
 * first — a type can be blocked for two independent causes and fixing one would
 * otherwise reveal the next only on the next attempt.
 *
 * `performerRoleNames` enriches `role_not_permitted` with the roles that WOULD
 * work ("Requires Manager or Owner"). It is optional because naming them needs
 * `org:roles:read`, which a manager holds and an inspector does not (P01
 * systemRoles) — so the picker degrades to the un-named sentence rather than
 * firing a 403 at the persona who most needs the message.
 */
export function ineligibilityReason(
  eligibility: TypeEligibility,
  performerRoleNames?: string[],
): string {
  const parts = eligibility.reasons.map((reason) => {
    if (reason === 'role_not_permitted' && performerRoleNames && performerRoleNames.length > 0) {
      return `${ELIGIBILITY_REASON_TEXT.role_not_permitted} Requires ${performerRoleNames.join(' or ')}.`;
    }
    return ELIGIBILITY_REASON_TEXT[reason];
  });
  return parts.join(' ');
}

// ── The no-eligible-type remedy split (P03 §7.2) ───────────────────────────
// "If no Type is eligible, UI distinguishes request-access vs activate-a-type
// vs publish-a-template." One generic empty state is the failure this contract
// names, so the cause is resolved from the SAME server reason codes and each
// cause gets its own sentence and its own link.

export type RemedyKind = 'no-type-for-domain' | 'permission' | 'role' | 'template';

export interface Remedy {
  kind: RemedyKind;
  title: string;
  description: string;
  /** Where the fix is made. Omitted when this principal cannot make it. */
  action?: { label: string; to: string };
}

export interface RemedyInput {
  /** The domain noun for the selected Site, from `useVocab()`. */
  domainNoun: string;
  /** The ACTIVE types the server returned for that domain (may be empty). */
  types: InspectionType[];
  /** Does this principal hold `types:manage`? */
  canManageTypes: boolean;
  /** Does this principal hold `templates:manage`? */
  canManageTemplates: boolean;
}

const has = (type: InspectionType, reason: EligibilityReason): boolean =>
  type.eligibility.reasons.includes(reason);

/**
 * Which of the three remedies applies, resolved most-specific-first:
 *
 *  1. **no type at all for this domain** — nothing to be eligible for; the fix
 *     is to create/activate one. Checked first because every other reason code
 *     presupposes a type to be blocked ON.
 *  2. **permission** — the caller holds no `inspections:create` at all, so
 *     EVERY type is blocked for the same reason and no amount of role or
 *     template work changes it. This is "request access".
 *  3. **template** — every blocked type is blocked by `no_selectable_template`;
 *     the fix is in Templates, not in access.
 *  4. **role** — the remaining case: types exist and are publishable, but this
 *     principal's role is not a performer on any of them.
 */
export function noEligibleRemedy({
  domainNoun,
  types,
  canManageTypes,
  canManageTemplates,
}: RemedyInput): Remedy {
  const typesLink = { label: 'Manage inspection types', to: '/app/settings/inspection-types' };
  const templatesLink = { label: 'Manage templates', to: '/app/report-presets' };

  if (types.length === 0) {
    return {
      kind: 'no-type-for-domain',
      title: `No inspection type for ${domainNoun.toLowerCase()} yet`,
      description: canManageTypes
        ? `Nothing has been set up to inspect a ${domainNoun.toLowerCase()} yet. Create an inspection type for this domain and activate it.`
        : `Nothing has been set up to inspect a ${domainNoun.toLowerCase()} yet. Ask an owner to create and activate one.`,
      ...(canManageTypes ? { action: typesLink } : {}),
    };
  }

  const blocked = types.filter((type) => !type.eligibility.canPerform);

  if (blocked.every((type) => has(type, 'permission_missing'))) {
    return {
      kind: 'permission',
      title: "You can't start inspections",
      description:
        "Your role doesn't include the create-inspection permission, so no inspection type is available to you. Ask an owner to grant it.",
      action: { label: 'Open access & roles', to: '/app/settings/access' },
    };
  }

  if (blocked.every((type) => has(type, 'no_selectable_template'))) {
    return {
      kind: 'template',
      title: 'No template is ready',
      description: canManageTemplates
        ? `Every inspection type for ${domainNoun.toLowerCase()} is linked to templates that are not selectable yet. Publish one and the type becomes available.`
        : `Every inspection type for ${domainNoun.toLowerCase()} is linked to templates that are not selectable yet. Ask a template editor to publish one.`,
      ...(canManageTemplates ? { action: templatesLink } : {}),
    };
  }

  return {
    kind: 'role',
    title: 'Your role cannot perform these',
    description: canManageTypes
      ? `Inspection types exist for ${domainNoun.toLowerCase()}, but none names a role you hold as a performer. Add your role to one, or ask an owner to.`
      : `Inspection types exist for ${domainNoun.toLowerCase()}, but none names a role you hold as a performer. Ask an owner to add your role to one.`,
    ...(canManageTypes ? { action: typesLink } : {}),
  };
}
